import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import { useAuth } from './AuthContext';
import {
  requestNotificationPermission,
  removeNotificationToken,
  setupForegroundListener,
} from '../services/notificationService';

const NotificationContext = createContext(null);

// ─── Auth token helper (double-base64 encoded, same as api.js) ───────────────
const decodeToken = (encodedToken) => {
  try { return atob(atob(encodedToken)); } catch { return null; }
};
const getAuthToken = () => {
  const encoded = localStorage.getItem('token');
  return encoded ? decodeToken(encoded) : null;
};

// ─── Map a DB InAppNotification record → internal notification shape ─────────
// Backend model fields: id, title, message, type, referenceType, referenceId,
//   data (JSON), isRead, readAt, createdAt
// Header.js expects notification.data to contain: shipmentType, awbNo / blNumber,
//   origin, destination, portCode, portOfDischargeCode, customer, exporterName,
//   grWeight, totalPkt, gwt, pkg, sbDate, username
const mapDbNotification = (n) => ({
  id: n.id,
  type: n.type,                 // shipment_created | shipment_updated
  title: n.title,
  message: n.message,
  referenceType: n.referenceType, // air_export_shipment | sea_export_shipment
  referenceId: n.referenceId,
  isRead: n.isRead || false,
  createdAt: n.createdAt,
  data: n.data || {},            // JSON blob from DB — contains all shipment fields
});

// ─── Map a FCM foreground payload → internal notification shape ──────────────
// FCM data values are all strings. Backend sends referenceType, referenceId etc.
// We also try to parse the embedded 'data' JSON if backend sent it.
const mapFCMPayload = (payload) => {
  const { title, body } = payload.notification || {};
  const raw = payload.data || {};

  // Backend may have sent the full data blob as a JSON string
  let extraData = {};
  try {
    if (raw.data) extraData = JSON.parse(raw.data);
  } catch { /* ignore */ }

  // Derive shipmentType for Header dropdown compatibility
  const refType = raw.referenceType || extraData.referenceType || '';
  const isAir = refType.includes('air');
  const shipmentType = extraData.shipmentType || (isAir ? 'Air-Export' : 'Sea-Export');

  return {
    id: raw.notificationId ? parseInt(raw.notificationId, 10) : `fcm_${Date.now()}`,
    type: raw.type || extraData.type || 'shipment_created',
    title: title || raw.title || '',
    message: body || raw.body || '',
    referenceType: refType,
    referenceId: raw.referenceId || extraData.referenceId || '',
    isRead: false,
    createdAt: new Date().toISOString(),
    // Merge everything into data so Header.js can read shipmentType, awbNo, etc.
    data: {
      shipmentType,
      ...extraData,
      ...raw,
    },
  };
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const mountedRef = useRef(true);
  const hasFetchedRef = useRef(false);
  const fcmUnsubscribeRef = useRef(null);

  // ─── Add notification to state (deduplicated by id) ───────────────────────
  const addNotification = useCallback((notification) => {
    setNotifications(prev => {
      if (prev.some(n => String(n.id) === String(notification.id))) return prev;
      return [notification, ...prev];
    });
    if (!notification.isRead) {
      setUnreadCount(prev => prev + 1);
    }
  }, []);

  // ─── Fetch all unread notifications from DB on login ─────────────────────
  const fetchUnreadNotifications = useCallback(async () => {
    if (hasFetchedRef.current) return;
    const token = getAuthToken();
    if (!token) return;

    try {
      console.log('🔔 Fetching unread notifications from DB...');
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.NOTIFICATION_LIST}?unreadOnly=true&limit=50`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();

      if (result.success) {
        const notifications = (result.data?.notifications || []).map(mapDbNotification);
        setNotifications(notifications);
        setUnreadCount(result.data?.unreadCount ?? notifications.filter(n => !n.isRead).length);
        console.log(`🔔 Loaded ${notifications.length} unread notifications from DB`);
      }

      hasFetchedRef.current = true;
    } catch (error) {
      console.warn('🔔 Failed to fetch notifications:', error.message);
    }
  }, []);

  // ─── FCM foreground message handler (tab is open) ────────────────────────
  const handleFCMForeground = useCallback((payload) => {
    if (!mountedRef.current) return;
    console.log('📬 FCM foreground message:', payload);

    const notification = mapFCMPayload(payload);

    // If we have a real DB id — refresh from DB to get complete data
    if (typeof notification.id === 'number') {
      const token = getAuthToken();
      if (token) {
        fetch(`${API_BASE_URL}${API_ENDPOINTS.NOTIFICATION_LIST}?unreadOnly=true&limit=50`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then(r => r.json())
          .then(result => {
            if (!mountedRef.current) return;
            if (result.success) {
              const fresh = (result.data?.notifications || []).map(mapDbNotification);
              setNotifications(fresh);
              setUnreadCount(result.data?.unreadCount ?? fresh.filter(n => !n.isRead).length);
            }
          })
          .catch(() => {
            // Fallback: just add what FCM sent
            addNotification(notification);
          });
      }
    } else {
      addNotification(notification);
    }

    // Show toast banner
    const data = notification.data || {};
    const isAir = (data.shipmentType || '').includes('Air');
    const shipmentId = isAir ? data.awbNo : data.blNumber;
    const origin = isAir ? data.origin : data.portCode;
    const dest = isAir ? data.destination : data.portOfDischargeCode;

    toast(
      `New ${data.shipmentType || 'Shipment'}${shipmentId ? `\n${shipmentId}` : ''}${origin ? ` | ${origin} → ${dest}` : ''}`,
      {
        duration: 5000,
        position: 'top-right',
        icon: isAir ? '✈️' : '🚢',
        style: { background: '#1e293b', color: '#fff', fontSize: '13px', maxWidth: '360px' },
      }
    );
  }, [addNotification]);

  // ─── Actions ─────────────────────────────────────────────────────────────
  const markRead = useCallback((notificationId) => {
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));

    const token = getAuthToken();
    if (token && notificationId) {
      fetch(`${API_BASE_URL}${API_ENDPOINTS.NOTIFICATION_MARK_READ}/${notificationId}/read`, {
        method: 'PUT',   // backend uses PUT
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      }).catch(err => console.warn('🔔 Failed to mark notification read:', err.message));
    }
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    setUnreadCount(0);

    const token = getAuthToken();
    if (token) {
      fetch(`${API_BASE_URL}${API_ENDPOINTS.NOTIFICATION_READ_ALL}`, {
        method: 'PUT',   // backend uses PUT
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      }).catch(err => console.warn('🔔 Failed to mark all read:', err.message));
    }
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  const removeNotification = useCallback((notificationId) => {
    setNotifications(prev => {
      const target = prev.find(n => n.id === notificationId);
      if (target && !target.isRead) setUnreadCount(c => Math.max(0, c - 1));
      return prev.filter(n => n.id !== notificationId);
    });

    const token = getAuthToken();
    if (token && notificationId) {
      fetch(`${API_BASE_URL}${API_ENDPOINTS.NOTIFICATION_DELETE}/${notificationId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
  }, []);

  // ─── Setup on login ───────────────────────────────────────────────────────
  useEffect(() => {
    mountedRef.current = true;

    if (user?.id && (user.role === 'user' || user.role === 'accountant')) {
      console.log('🔔 Setting up notifications (FCM) for role:', user.role);

      // 1. Load existing unread from DB
      fetchUnreadNotifications();

      // 2. Request FCM permission + register token → setup foreground listener
      requestNotificationPermission().then((token) => {
        if (token && mountedRef.current) {
          fcmUnsubscribeRef.current = setupForegroundListener(handleFCMForeground);
          console.log('✅ FCM foreground listener active');
        }
      });
    }

    return () => {
      mountedRef.current = false;
      if (fcmUnsubscribeRef.current) {
        fcmUnsubscribeRef.current();
        fcmUnsubscribeRef.current = null;
      }
    };
  }, [user?.id, user?.role, fetchUnreadNotifications, handleFCMForeground]);

  // ─── Cleanup on logout ────────────────────────────────────────────────────
  useEffect(() => {
    if (!user) {
      if (fcmUnsubscribeRef.current) {
        fcmUnsubscribeRef.current();
        fcmUnsubscribeRef.current = null;
      }
      // Deactivate FCM token in backend so no more pushes to this browser
      removeNotificationToken();

      setNotifications([]);
      setUnreadCount(0);
      hasFetchedRef.current = false;
    }
  }, [user]);

  // ─── Highlighted shipment (scroll-to on notification click) ──────────────
  const [highlightedShipment, setHighlightedShipment] = useState(null);

  const value = {
    notifications,
    unreadCount,
    markRead,
    markAllRead,
    clearAll,
    removeNotification,
    highlightedShipment,
    setHighlightedShipment,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return ctx;
};

export default NotificationContext;
